import React, { useState, useEffect } from 'react';
import Header from '../components/layout/Header';
import { useLanguage } from '../contexts/LanguageContext';
import { useTheme } from '../contexts/ThemeContext';
import { useAuth } from '../contexts/AuthContext';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Switch } from '../components/ui/switch';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { Input } from '../components/ui/input';
import { 
  Sun, 
  Moon, 
  Languages, 
  User,
  Shield,
  Mail,
  Phone,
  Database,
  HardDrive,
  Info,
  Download,
  Upload,
  RefreshCw,
  AlertTriangle
} from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const SettingsPage = () => {
  const { t, language, toggleLanguage, direction } = useLanguage();
  const { isDark, toggleTheme } = useTheme();
  const { user, token, isAdmin } = useAuth();
  const [backupStats, setBackupStats] = useState(null);
  const [loadingBackup, setLoadingBackup] = useState(false);
  const [loadingStats, setLoadingStats] = useState(false);

  const api = axios.create({
    baseURL: `${API_URL}/api`,
    headers: { Authorization: `Bearer ${token}` }
  });

  useEffect(() => {
    if (isAdmin) {
      fetchBackupStats();
    }
  }, [isAdmin]);

  const fetchBackupStats = async () => {
    setLoadingStats(true);
    try {
      const res = await api.get('/backup/stats');
      setBackupStats(res.data);
    } catch (error) {
      console.error('Failed to fetch backup stats:', error);
    } finally {
      setLoadingStats(false);
    }
  };

  const handleExportBackup = async () => {
    setLoadingBackup(true);
    try {
      const response = await api.get('/backup/export', { responseType: 'blob' });
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `tamkeen_backup_${new Date().toISOString().split('T')[0]}.json`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      toast.success(language === 'ar' ? 'تم تصدير النسخة الاحتياطية' : 'Backup exported successfully');
    } catch (error) {
      console.error('Export error:', error);
      toast.error(language === 'ar' ? 'فشل تصدير النسخة الاحتياطية' : 'Failed to export backup');
    } finally {
      setLoadingBackup(false);
    }
  };

  const handleImportBackup = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    if (!window.confirm(language === 'ar' 
      ? 'تحذير: سيتم استبدال جميع البيانات الحالية. هل أنت متأكد؟' 
      : 'Warning: This will replace all current data. Are you sure?')) {
      return;
    }

    setLoadingBackup(true);
    try {
      const formData = new FormData();
      formData.append('file', file);
      const res = await api.post('/backup/import', formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      toast.success(language === 'ar' ? 'تم استيراد النسخة الاحتياطية بنجاح' : 'Backup imported successfully');
      fetchBackupStats();
    } catch (error) {
      console.error('Import error:', error);
      toast.error(language === 'ar' ? 'فشل استيراد النسخة الاحتياطية' : 'Failed to import backup');
    } finally {
      setLoadingBackup(false);
      e.target.value = '';
    }
  };

  return (
    <div data-testid="settings-page">
      <Header title={t('settings')} />
      
      <div className="p-6 space-y-6 max-w-4xl">
        {/* Appearance Settings */}
        <Card data-testid="appearance-settings">
          <CardHeader>
            <CardTitle className="text-lg flex items-center gap-2">
              {isDark ? <Moon className="w-5 h-5" /> : <Sun className="w-5 h-5" />}
              {t('theme')}
            </CardTitle>
            <CardDescription>
              {language === 'ar' ? 'تخصيص مظهر التطبيق' : 'Customize the application appearance'}
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="flex items-center justify-between">
              <div className="space-y-0.5">
                <Label className="text-base">{t('darkMode')}</Label>
                <p className="text-sm text-muted-foreground">
                  {language === 'ar' ? 'تفعيل الوضع الداكن للتطبيق' : 'Enable dark mode for the application'}
                </p>
              </div>
              <Switch
                checked={isDark}
                onCheckedChange={toggleTheme}
                data-testid="theme-switch"
              />
            </div>
          </CardContent>
        </Card>

        {/* Language Settings */}
        <Card data-testid="language-settings">
          <CardHeader>
            <CardTitle className="text-lg flex items-center gap-2">
              <Languages className="w-5 h-5" />
              {t('language')}
            </CardTitle>
            <CardDescription>
              {language === 'ar' ? 'اختر لغة التطبيق' : 'Choose the application language'}
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="flex gap-3">
              <Button
                variant={language === 'en' ? 'default' : 'outline'}
                onClick={() => language !== 'en' && toggleLanguage()}
                className="flex-1"
                data-testid="lang-english-btn"
              >
                English
              </Button>
              <Button
                variant={language === 'ar' ? 'default' : 'outline'}
                onClick={() => language !== 'ar' && toggleLanguage()}
                className="flex-1"
                data-testid="lang-arabic-btn"
              >
                العربية
              </Button>
            </div>
            <p className="text-sm text-muted-foreground mt-3">
              {language === 'ar' 
                ? `الاتجاه الحالي: من اليمين إلى اليسار (RTL)` 
                : `Current direction: Left to Right (LTR)`}
            </p>
          </CardContent>
        </Card>

        {/* Profile Info */}
        <Card data-testid="profile-info">
          <CardHeader>
            <CardTitle className="text-lg flex items-center gap-2">
              <User className="w-5 h-5" />
              {t('profile')}
            </CardTitle>
            <CardDescription>
              {language === 'ar' ? 'معلومات حسابك' : 'Your account information'}
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="flex items-center gap-4">
              <div className="w-16 h-16 rounded-full bg-primary/10 flex items-center justify-center">
                <User className="w-8 h-8 text-primary" strokeWidth={1.5} />
              </div>
              <div>
                <h3 className="text-lg font-semibold">{user?.full_name}</h3>
                <p className="text-sm text-muted-foreground">@{user?.username}</p>
              </div>
            </div>
            
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 pt-4 border-t">
              <div className="flex items-center gap-3">
                <Shield className="w-5 h-5 text-muted-foreground" strokeWidth={1.5} />
                <div>
                  <p className="text-sm text-muted-foreground">{t('role')}</p>
                  <Badge className="mt-1">
                    {user?.role === 'admin' ? t('admin') : 
                     user?.role === 'lawyer' ? t('lawyer') : t('assistant')}
                  </Badge>
                </div>
              </div>
              <div className="flex items-center gap-3">
                <Mail className="w-5 h-5 text-muted-foreground" strokeWidth={1.5} />
                <div>
                  <p className="text-sm text-muted-foreground">{t('email')}</p>
                  <p className="font-medium">{user?.email}</p>
                </div>
              </div>
              {user?.phone && (
                <div className="flex items-center gap-3">
                  <Phone className="w-5 h-5 text-muted-foreground" strokeWidth={1.5} />
                  <div>
                    <p className="text-sm text-muted-foreground">{t('phone')}</p>
                    <p className="font-medium">{user?.phone}</p>
                  </div>
                </div>
              )}
            </div>
          </CardContent>
        </Card>

        {/* Backup & Restore - Admin Only */}
        {isAdmin && (
          <Card data-testid="backup-settings">
            <CardHeader>
              <CardTitle className="text-lg flex items-center gap-2">
                <Database className="w-5 h-5" />
                {language === 'ar' ? 'النسخ الاحتياطي والاستعادة' : 'Backup & Restore'}
              </CardTitle>
              <CardDescription>
                {language === 'ar' ? 'إدارة النسخ الاحتياطية لبيانات النظام' : 'Manage system data backups'}
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-6">
              {/* Database Stats */}
              {backupStats && (
                <div className="p-4 rounded-lg bg-muted/50 space-y-3">
                  <div className="flex items-center justify-between">
                    <span className="text-sm font-medium">
                      {language === 'ar' ? 'إحصائيات قاعدة البيانات' : 'Database Statistics'}
                    </span>
                    <Button 
                      variant="ghost" 
                      size="sm" 
                      onClick={fetchBackupStats}
                      disabled={loadingStats}
                    >
                      <RefreshCw className={`w-4 h-4 ${loadingStats ? 'animate-spin' : ''}`} />
                    </Button>
                  </div>
                  <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 text-sm">
                    <div className="p-2 rounded bg-background">
                      <p className="text-muted-foreground">{language === 'ar' ? 'المستخدمين' : 'Users'}</p>
                      <p className="font-semibold">{backupStats.stats?.users || 0}</p>
                    </div>
                    <div className="p-2 rounded bg-background">
                      <p className="text-muted-foreground">{language === 'ar' ? 'العملاء' : 'Clients'}</p>
                      <p className="font-semibold">{backupStats.stats?.clients || 0}</p>
                    </div>
                    <div className="p-2 rounded bg-background">
                      <p className="text-muted-foreground">{language === 'ar' ? 'القضايا' : 'Cases'}</p>
                      <p className="font-semibold">{backupStats.stats?.cases || 0}</p>
                    </div>
                    <div className="p-2 rounded bg-background">
                      <p className="text-muted-foreground">{language === 'ar' ? 'المعاملات' : 'Transactions'}</p>
                      <p className="font-semibold">{backupStats.stats?.transactions || 0}</p>
                    </div>
                  </div>
                  <p className="text-xs text-muted-foreground">
                    {language === 'ar' ? 'إجمالي السجلات:' : 'Total records:'} {backupStats.total_records || 0}
                  </p>
                </div>
              )}

              {/* Export Backup */}
              <div className="flex items-center justify-between p-4 rounded-lg border">
                <div>
                  <h4 className="font-medium flex items-center gap-2">
                    <Download className="w-4 h-4" />
                    {language === 'ar' ? 'تصدير نسخة احتياطية' : 'Export Backup'}
                  </h4>
                  <p className="text-sm text-muted-foreground">
                    {language === 'ar' ? 'تحميل جميع البيانات كملف JSON' : 'Download all data as a JSON file'}
                  </p>
                </div>
                <Button 
                  onClick={handleExportBackup}
                  disabled={loadingBackup}
                  className="btn-accent"
                  data-testid="export-backup-btn"
                >
                  {loadingBackup ? (
                    <RefreshCw className="w-4 h-4 animate-spin" />
                  ) : (
                    <Download className="w-4 h-4 mr-2" />
                  )}
                  {language === 'ar' ? 'تصدير' : 'Export'}
                </Button>
              </div>

              {/* Import Backup */}
              <div className="flex items-center justify-between p-4 rounded-lg border border-amber-200 dark:border-amber-800 bg-amber-50 dark:bg-amber-900/20">
                <div>
                  <h4 className="font-medium flex items-center gap-2">
                    <Upload className="w-4 h-4" />
                    {language === 'ar' ? 'استعادة من نسخة احتياطية' : 'Restore from Backup'}
                  </h4>
                  <p className="text-sm text-muted-foreground flex items-center gap-1">
                    <AlertTriangle className="w-3 h-3 text-amber-500" />
                    {language === 'ar' ? 'سيتم استبدال البيانات الحالية' : 'This will replace current data'}
                  </p>
                </div>
                <div>
                  <Input
                    type="file"
                    accept=".json"
                    onChange={handleImportBackup}
                    className="hidden"
                    id="backup-file"
                    disabled={loadingBackup}
                  />
                  <Label htmlFor="backup-file" className="cursor-pointer">
                    <Button 
                      variant="outline"
                      disabled={loadingBackup}
                      asChild
                      data-testid="import-backup-btn"
                    >
                      <span>
                        {loadingBackup ? (
                          <RefreshCw className="w-4 h-4 animate-spin" />
                        ) : (
                          <Upload className="w-4 h-4 mr-2" />
                        )}
                        {language === 'ar' ? 'استيراد' : 'Import'}
                      </span>
                    </Button>
                  </Label>
                </div>
              </div>
            </CardContent>
          </Card>
        )}

        {/* System Info */}
        <Card data-testid="system-info">
          <CardHeader>
            <CardTitle className="text-lg flex items-center gap-2">
              <Info className="w-5 h-5" />
              {language === 'ar' ? 'معلومات النظام' : 'System Information'}
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-3">
            <div className="flex items-center justify-between py-2 border-b">
              <div className="flex items-center gap-2">
                <Database className="w-4 h-4 text-muted-foreground" />
                <span className="text-sm">{language === 'ar' ? 'قاعدة البيانات' : 'Database'}</span>
              </div>
              <Badge variant="outline" className="text-emerald-600">
                {language === 'ar' ? 'متصل' : 'Connected'}
              </Badge>
            </div>
            <div className="flex items-center justify-between py-2 border-b">
              <div className="flex items-center gap-2">
                <HardDrive className="w-4 h-4 text-muted-foreground" />
                <span className="text-sm">{language === 'ar' ? 'التخزين' : 'Storage'}</span>
              </div>
              <Badge variant="outline">
                {language === 'ar' ? 'محلي' : 'Local'}
              </Badge>
            </div>
            <div className="flex items-center justify-between py-2">
              <span className="text-sm text-muted-foreground">
                {language === 'ar' ? 'الإصدار' : 'Version'}
              </span>
              <span className="text-sm font-mono">1.0.0</span>
            </div>
          </CardContent>
        </Card>

        {/* Offline Notice */}
        <Card className="bg-muted/50 border-dashed" data-testid="offline-notice">
          <CardContent className="py-4">
            <div className="flex items-start gap-3">
              <div className="p-2 rounded-sm bg-[hsl(var(--accent))]/10">
                <HardDrive className="w-5 h-5 text-[hsl(var(--accent))]" strokeWidth={1.5} />
              </div>
              <div>
                <h4 className="font-medium">
                  {language === 'ar' ? 'النظام يعمل بدون إنترنت' : 'System works offline'}
                </h4>
                <p className="text-sm text-muted-foreground mt-1">
                  {language === 'ar' 
                    ? 'جميع البيانات مخزنة محلياً. لا يتطلب النظام اتصالاً بالإنترنت للعمل.'
                    : 'All data is stored locally. The system does not require an internet connection to operate.'}
                </p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default SettingsPage;
